import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Grid
} from '@mui/material';
import {
  Description,
  Engineering,
  VerifiedUser,
  Storefront,
  ArrowForward,
  ChatBubbleOutline,
  CheckCircle,
  HourglassEmpty,
  Cancel,
  Visibility,
  AttachMoney
} from '@mui/icons-material';
import api from '../services/api';
import { brand, text } from '../theme/colors';

/**
 * Unified client Offers inbox.
 *
 * Aggregates every kind of offer a project owner can receive — contractor
 * quotations (bids), contractor work proposals, verifier quotes, and
 * supplier material quotes — into one list, grouped by project. Each row
 * deep-links into the existing review/accept flow for its source system;
 * this page never accepts anything itself.
 */

const SOURCE_META = {
  bid: { label: 'CONTRACTOR QUOTATION', icon: <Description fontSize="small" />, color: 'primary' },
  proposal: { label: 'WORK PROPOSAL', icon: <Engineering fontSize="small" />, color: 'info' },
  verifier: { label: 'VERIFICATION QUOTE', icon: <VerifiedUser fontSize="small" />, color: 'secondary' },
  merchant: { label: 'MATERIAL QUOTES', icon: <Storefront fontSize="small" />, color: 'warning' }
};

const statusIcon = (status) => {
  if (['accepted', 'approved', 'completed'].includes(status)) return <CheckCircle />;
  if (['rejected', 'declined', 'expired', 'withdrawn'].includes(status)) return <Cancel />;
  return <HourglassEmpty />;
};

// Statuses that mean "the ball is in the client's court". Deliberately
// excludes merchant 'sent' (waiting on suppliers) and proposal
// 'negotiating' (client already requested changes; waiting on contractor).
const NEEDS_REVIEW_STATUSES = new Set([
  'submitted', 'pending', 'under_review', 'quotes_received'
]);

const formatMoney = (amount, currency = 'USD') => {
  const n = parseFloat(amount);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${currency} ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

const statusChipColor = (status) => {
  if (['accepted', 'approved', 'completed'].includes(status)) return 'success';
  if (['rejected', 'declined', 'expired', 'withdrawn'].includes(status)) return 'default';
  if (NEEDS_REVIEW_STATUSES.has(status)) return 'warning';
  return 'default';
};

const OffersPage = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [chatDetectionCount, setChatDetectionCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState([]);
  const [filter, setFilter] = useState('needs_review');

  const loadOffers = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      api.get('/bids/client/me'),
      api.get('/proposals/client/me'),
      api.get('/client/verifier-proposals'),
      api.get('/merchant-quotes/requests'),
      api.get('/bids/chat-detections/mine')
    ]);

    const [bidsRes, proposalsRes, verifierRes, merchantRes, chatRes] = results;
    const errors = [];
    const collected = [];

    if (bidsRes.status === 'fulfilled') {
      const bids = bidsRes.value?.data?.data || [];
      bids.forEach((b) => collected.push({
        key: `bid-${b.id}`,
        source: 'bid',
        title: b.bid_number ? `Quotation ${b.bid_number}` : 'Contractor quotation',
        project: b.project_name || 'Project',
        from: b.contractor_name || 'Contractor',
        amount: formatMoney(b.total_cost, b.currency),
        status: b.status,
        date: b.submitted_at || b.created_at,
        href: `/bids/${b.id}`
      }));
    } else {
      errors.push('contractor quotations');
    }

    if (proposalsRes.status === 'fulfilled') {
      const proposals = proposalsRes.value?.data?.data || [];
      proposals.forEach((p) => collected.push({
        key: `proposal-${p.id}`,
        source: 'proposal',
        title: 'Work plan & milestones',
        project: p.projectTitle || 'Project',
        from: p.contractorName || 'Contractor',
        amount: formatMoney(p.totalCost, p.currency),
        status: p.status,
        date: p.submittedAt || p.createdAt,
        href: `/projects/${p.projectId}/proposals`
      }));
    } else {
      errors.push('work proposals');
    }

    if (verifierRes.status === 'fulfilled') {
      const proposals = verifierRes.value?.data?.data || [];
      proposals.forEach((v) => collected.push({
        key: `verifier-${v.id}`,
        source: 'verifier',
        title: v.milestone_name ? `Verify: ${v.milestone_name}` : 'Verification quote',
        project: v.project_title || 'Verification',
        from: [v.verifier_first_name, v.verifier_last_name].filter(Boolean).join(' ') || 'Verifier',
        amount: formatMoney(v.quote_amount, v.currency || 'USD'),
        status: v.status,
        date: v.created_at,
        href: `/verification-requests/${v.request_id}/proposals`
      }));
    } else {
      errors.push('verification quotes');
    }

    if (merchantRes.status === 'fulfilled') {
      const requests = merchantRes.value?.data?.data || [];
      requests.forEach((r) => {
        const quoteCount = parseInt(r.quotes_received_count || 0, 10);
        collected.push({
          key: `merchant-${r.id}`,
          source: 'merchant',
          title: 'Material quote request',
          project: r.project_title || 'Materials',
          from: quoteCount > 0 ? `${quoteCount} supplier quote${quoteCount > 1 ? 's' : ''}` : 'Awaiting supplier quotes',
          amount: null,
          status: quoteCount > 0 && r.status === 'sent' ? 'quotes_received' : r.status,
          date: r.created_at,
          href: `/quotes/${r.id}`
        });
      });
    } else {
      errors.push('material quotes');
    }

    if (chatRes.status === 'fulfilled') {
      const detections = chatRes.value?.data?.data || [];
      // Only detections the client hasn't already turned into an official
      // quotation request (requestedAt is stamped when they do)
      setChatDetectionCount(
        Array.isArray(detections) ? detections.filter((d) => !d.requestedAt).length : 0
      );
    }

    collected.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    setOffers(collected);
    setLoadErrors(errors);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const filtered = useMemo(() => {
    if (filter === 'all') return offers;
    if (filter === 'needs_review') return offers.filter((o) => NEEDS_REVIEW_STATUSES.has(o.status));
    if (filter === 'accepted') return offers.filter((o) => ['accepted', 'approved', 'completed'].includes(o.status));
    return offers;
  }, [offers, filter]);

  const needsReviewCount = offers.filter((o) => NEEDS_REVIEW_STATUSES.has(o.status)).length;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Quotes
          </Typography>
          <Typography variant="body2" sx={{ color: text.secondary }}>
            Every quotation, proposal, and quote you&apos;ve received, in one place
          </Typography>
        </Box>
        <Button
          variant="outlined"
          endIcon={<ArrowForward />}
          onClick={() => navigate('/my-quote-requests')}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Quote requests I sent
        </Button>
      </Box>

      {/* Filters */}
      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Chip
          label={`Needs your review${needsReviewCount ? ` (${needsReviewCount})` : ''}`}
          color={filter === 'needs_review' ? 'primary' : 'default'}
          onClick={() => setFilter('needs_review')}
        />
        <Chip
          label="Accepted"
          color={filter === 'accepted' ? 'primary' : 'default'}
          onClick={() => setFilter('accepted')}
        />
        <Chip
          label="All"
          color={filter === 'all' ? 'primary' : 'default'}
          onClick={() => setFilter('all')}
        />
      </Stack>

      {loadErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Some offers could not be loaded right now ({loadErrors.join(', ')}). Pull to refresh or try again shortly.
        </Alert>
      )}

      {chatDetectionCount > 0 && (
        <Alert
          severity="info"
          icon={<ChatBubbleOutline />}
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => navigate('/bids/review')}
          action={<Button size="small" sx={{ textTransform: 'none' }}>Review</Button>}
        >
          {chatDetectionCount} informal quote{chatDetectionCount > 1 ? 's' : ''} mentioned in your chats — ask for an official quotation
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {filter === 'needs_review' ? 'Nothing waiting on you' : 'No offers yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {filter === 'needs_review'
                ? 'When a contractor, verifier, or supplier sends you a quote, it appears here.'
                : 'Post a project or invite contractors, and their quotations will land here.'}
            </Typography>
            {filter !== 'needs_review' ? (
              <Button
                variant="contained"
                onClick={() => navigate('/contractors')}
                sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark }, textTransform: 'none', fontWeight: 600 }}
              >
                Find contractors
              </Button>
            ) : (
              <Button variant="text" onClick={() => setFilter('all')} sx={{ textTransform: 'none' }}>
                View all offers
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {filtered.map((offer) => {
            const meta = SOURCE_META[offer.source];
            return (
              <Card
                key={offer.key}
                sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
                onClick={() => navigate(offer.href)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      {/* Source, title, and status */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Chip label={meta.label} size="small" color={meta.color} variant="outlined" />
                        <Typography variant="h6">{offer.title}</Typography>
                        <Chip
                          label={(offer.status || 'pending').replace(/_/g, ' ').toUpperCase()}
                          color={statusChipColor(offer.status)}
                          size="small"
                          icon={statusIcon(offer.status)}
                        />
                      </Box>

                      {/* Project */}
                      <Typography variant="body1" gutterBottom>
                        {offer.project}
                      </Typography>

                      {/* From */}
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        From: {offer.from}
                      </Typography>

                      {/* Key details */}
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        {offer.amount && (
                          <Grid item xs={12} sm={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AttachMoney fontSize="small" color="action" />
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Total
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {offer.amount}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        )}
                        {offer.date && (
                          <Grid item xs={12} sm={4}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Received
                              </Typography>
                              <Typography variant="body2">{formatDate(offer.date)}</Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<Visibility />}
                        onClick={(e) => { e.stopPropagation(); navigate(offer.href); }}
                        sx={{ background: brand.gradient, whiteSpace: 'nowrap' }}
                      >
                        Review
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Container>
  );
};

export default OffersPage;
